import { useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { loadGroupRankings, type GroupRankingRow } from "@/lib/ranking";
import { mediaUrl } from "@/lib/media";
import { GROUP_PRIZES, PRIZE_DEADLINE } from "@/lib/constants";
import { motion } from "framer-motion";
import { Users, TrendingUp, TrendingDown, Trophy, Flame, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Countdown } from "@/components/Countdown";
import { loadTrend, maybeSaveTrend, getDelta } from "@/lib/groupRaceTrend";

export function RacePodium() {
  const { user } = useAuth();
  const [groups, setGroups] = useState<GroupRankingRow[]>([]);
  const [myGroupIds, setMyGroupIds] = useState<Set<string>>(new Set());
  const [prev] = useState(() => loadTrend());

  useEffect(() => {
    let alive = true;
    (async () => {
      const gRanks = await loadGroupRankings();
      if (!alive) return;
      setGroups(gRanks);
      maybeSaveTrend(gRanks);
      if (user) {
        const groups = (await api.groups.mine()) as any[];
        if (alive) setMyGroupIds(new Set((groups ?? []).map((g: any) => g.id)));
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const podium = groups.slice(0, 3);
  const myGroups = groups.filter((g) => myGroupIds.has(g.group_id));
  const myBest = myGroups.length
    ? myGroups.reduce((a, b) => (a.rank <= b.rank ? a : b))
    : null;

  // Threat: nächste Gruppe hinter mir (wenn Top-3)
  const threatBehind = useMemo(() => {
    if (!myBest || myBest.rank > 3) return null;
    const next = groups.find((g) => g.rank === myBest.rank + 1);
    if (!next) return null;
    const gap = myBest.total_points - next.total_points;
    return { name: next.name, gap: Math.max(0, gap) };
  }, [groups, myBest]);

  // Boost: was muss ich aufholen, wenn ich knapp dran bin?
  const reachAhead = useMemo(() => {
    if (!myBest || myBest.rank === 1) return null;
    const ahead = groups.find((g) => g.rank === myBest.rank - 1);
    if (!ahead) return null;
    const gap = ahead.total_points - myBest.total_points;
    return { name: ahead.name, gap: Math.max(1, gap) };
  }, [groups, myBest]);

  const maxPoints = Math.max(1, ...podium.map((g) => g.total_points));
  const days = Math.max(0, Math.ceil((new Date(PRIZE_DEADLINE).getTime() - Date.now()) / 86_400_000));
  const endspurt = days <= 7;

  return (
    <div id="race-podium" className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 px-1">
        <div>
          <div className="flex items-center gap-1.5">
            <Trophy className="h-5 w-5 text-accent-foreground" />
            <h2 className="font-display font-extrabold text-lg leading-none">Das winkt deiner Tippgruppe</h2>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 leading-tight">
            <b>5.000 €</b> Englputzeder-Sponsoring für die <b>Top-3</b>.
          </p>
        </div>
        <div className="text-right shrink-0">
          {endspurt ? (
            <span className="inline-flex items-center gap-1 bg-destructive text-destructive-foreground rounded-pill px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
              <Flame className="h-3 w-3" /> Endspurt
            </span>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              Vergabe in
            </span>
          )}
          <div className="text-[11px] font-bold text-foreground tabular-nums mt-0.5">
            <Countdown target={PRIZE_DEADLINE} compact />
          </div>
        </div>
      </div>

      {/* Preis-Vitrine: immer sichtbar */}
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => {
          const prize = GROUP_PRIZES[i];
          const leader = podium[i];
          const tone = i === 0 ? "bg-gold" : i === 1 ? "bg-silver" : "bg-bronze";
          const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : "🥉";
          const mine = leader && myGroupIds.has(leader.group_id);
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className={`relative overflow-hidden rounded-lg shadow-pop ${tone} p-3 flex flex-col items-center text-center ${i === 0 ? "ring-2 ring-foreground/10" : ""}`}
            >
              <div className="absolute -right-3 -top-3 text-5xl opacity-15 select-none">🏆</div>
              <div className="text-3xl leading-none">{medal}</div>
              <div className="text-[10px] font-bold uppercase tracking-wider mt-1 opacity-80">
                Platz {prize.rank}
              </div>
              <div className="font-display font-extrabold text-xl leading-tight tabular-nums mt-0.5">
                {prize.short}
              </div>
              <div className="text-[9px] font-semibold opacity-80 leading-tight">Sponsoring</div>

              <div className="w-full mt-2 pt-2 border-t border-foreground/15 min-h-[42px] flex items-center justify-center">
                {leader && leader.total_points > 0 ? (
                  <Link to={`/gruppe/${leader.slug}`} className="flex items-center gap-1 min-w-0 tap">
                    {leader.logo_url ? (
                      <img
                        src={mediaUrl(leader.logo_url)}
                        alt=""
                        className="h-5 w-5 rounded object-cover shrink-0"
                        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                      />
                    ) : (
                      <span className="text-sm">🏆</span>
                    )}
                    <span className="text-[10px] font-bold truncate max-w-[70px]">{leader.name}</span>
                    {mine && (
                      <span className="text-[8px] font-extrabold bg-foreground/15 rounded-pill px-1 shrink-0">DU</span>
                    )}
                    <span className="text-[10px] font-extrabold tabular-nums opacity-80">{leader.total_points}</span>
                  </Link>
                ) : (
                  <span className="text-[10px] font-bold opacity-60">noch frei</span>
                )}
              </div>
            </motion.div>
          );
        })}
      </div>

      {groups.length > 0 && podium[0]?.total_points === 0 && (
        <p className="text-[11px] text-muted-foreground text-center px-1">
          Aktuell <b className="text-foreground">{groups.length}</b>{" "}
          Tippgruppe{groups.length === 1 ? "" : "n"} am Start. Sobald die ersten Spiele gewertet sind, füllen sich die Plätze.
        </p>
      )}

      {/* Personal status / Threat */}
      {!user || myGroups.length === 0 ? (
        <Link
          to="/gruppen"
          className="block rounded-md bg-accent/15 border border-accent/30 p-3 text-center text-sm font-bold text-foreground tap"
        >
          👥 Tritt einer Tippgruppe bei und kämpft gemeinsam um <b className="tabular-nums">5.000 €</b> →
        </Link>
      ) : myBest && myBest.total_points > 0 ? (
        <div className="space-y-2">
          {myBest.rank <= 3 && (
            <div
              className={`rounded-md p-3 ${
                myBest.rank === 1 ? "bg-gold" : myBest.rank === 2 ? "bg-silver" : "bg-bronze"
              }`}
            >
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                <div className="font-bold text-sm">
                  <b>{myBest.name}</b> hält Rang {myBest.rank} ·{" "}
                  <span className="tabular-nums">{GROUP_PRIZES[myBest.rank - 1].short}</span> Sponsoring
                </div>
              </div>
              {threatBehind && (
                <div className="text-[11px] mt-1 opacity-90">
                  🔥 <b>{threatBehind.name}</b> ist nur {threatBehind.gap} Pkt hinter euch — verteidigen!
                </div>
              )}
            </div>
          )}
          {myBest && myBest.rank > 3 && reachAhead && (
            <div className="rounded-md bg-card border border-border p-3 flex items-center gap-2">
              <Flame className="h-4 w-4 text-destructive shrink-0" />
              <div className="text-[12px] leading-tight">
                <b>{myBest.name}</b> ist auf #{myBest.rank}. Noch{" "}
                <b className="tabular-nums text-foreground">{reachAhead.gap}</b> Pkt bis Platz{" "}
                {myBest.rank - 1} ({reachAhead.name}).
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Mini-Leiste: Mitglied = Hebel */}
      <div className="rounded-md bg-card border border-border px-3 py-2 flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground shrink-0" />
        <p className="text-[11px] text-muted-foreground leading-tight flex-1">
          Mehr aktive Tipper in der Gruppe = mehr Punkte. Jeder Tipp zählt fürs Gruppen-Sponsoring.
        </p>
      </div>
    </div>
  );
}
